import { getExtensionValue, MedplumClient, resolveId } from "@medplum/core";
import { Encounter, EncounterParticipant } from "@medplum/fhirtypes";

import { OTHERS_PRESENT_UNKNOWN_RELATIONSHIP } from "@/encounters/constants/others-present-relationship";
import {
  buildReferenceDisplayMap,
  participantHasTypeCode,
} from "@/encounters/utils/encounter-participants";
import { Extensions } from "@/fhir/enums/extensions";
import { EncounterPractitionerType } from "@/fhir/enums/quilted-health";
import { getPractitionerName } from "@/utils/medplum";

/**
 * Resolves a participant display name with the same preference order as
 * provider/birthing-assistant helpers: Practitioner lookup first, then display /
 * reference-map fallback.
 */
async function resolveParticipantDisplayName(
  medplum: MedplumClient,
  participant: EncounterParticipant,
  referenceDisplayMap: Map<string, string>,
): Promise<string> {
  const individual = participant.individual;
  if (!individual?.reference) {
    return individual?.display?.trim() || "";
  }

  try {
    const practitionerId = resolveId(individual);
    if (practitionerId) {
      const [practitionerName] = await getPractitionerName(medplum, practitionerId);
      if (practitionerName) return practitionerName;
    }
  } catch {
    // Expected when AccessPolicy blocks Practitioner reads. Match provider/BA
    // footer behavior: fall back to individual.display or the reference map.
  }

  return individual.display?.trim() || referenceDisplayMap.get(individual.reference) || "";
}

/**
 * True for legacy `doula` and current `others-present` participant type codes.
 */
export function isDoulaOrOthersPresent(participant: EncounterParticipant): boolean {
  return (
    participantHasTypeCode(participant, EncounterPractitionerType.Doula) ||
    participantHasTypeCode(participant, EncounterPractitionerType.OthersPresent)
  );
}

/**
 * Appends relationship-to-client for footer display.
 * Legacy doula-typed rows without an extension show " (Doula)".
 * Others-present rows missing the extension show " (Relationship unknown)".
 */
function appendOthersPresentRelationship(
  displayName: string,
  participant: EncounterParticipant,
): string {
  const relationshipToClient = getExtensionValue(
    participant,
    Extensions.EncounterParticipantRelationshipToClient,
  );
  if (typeof relationshipToClient === "string" && relationshipToClient.trim()) {
    return `${displayName} (${relationshipToClient.trim()})`;
  }

  if (participantHasTypeCode(participant, EncounterPractitionerType.Doula)) {
    return `${displayName} (Doula)`;
  }

  return `${displayName} (Relationship unknown)`;
}

/**
 * Formats others-present and legacy doula participants for the labor footer.
 * Resolves Practitioner references when display is missing.
 */
export const getOthersPresentNames = async (medplum: MedplumClient, encounter: Encounter) => {
  const othersPresent =
    encounter.participant?.filter((participant) => isDoulaOrOthersPresent(participant)) || [];

  const referenceDisplayMap = buildReferenceDisplayMap(encounter);
  const names = await Promise.all(
    othersPresent.map(async (participant) => {
      const displayName = await resolveParticipantDisplayName(
        medplum,
        participant,
        referenceDisplayMap,
      );
      if (!displayName) return "";
      return appendOthersPresentRelationship(displayName, participant);
    }),
  );

  return names.filter((name) => name);
};

/**
 * Loads others-present and legacy doula participants into the Before Charting form shape.
 * Resolves Practitioner references when display is missing so edit/save does not drop rows.
 * Throws when any legacy row cannot be resolved so callers can block save.
 */
export async function mapOthersPresentSelectionsFromEncounter(
  medplum: MedplumClient,
  encounter: Encounter,
): Promise<{ name: string; relationshipToClient: string; reference?: string }[]> {
  const referenceDisplayMap = buildReferenceDisplayMap(encounter);
  const othersParticipants =
    encounter.participant?.filter((participant) => isDoulaOrOthersPresent(participant)) ?? [];

  const selections = await Promise.all(
    othersParticipants.map(async (participant) => {
      const name = await resolveParticipantDisplayName(medplum, participant, referenceDisplayMap);
      if (!name) return null;

      const isDoula = participantHasTypeCode(participant, EncounterPractitionerType.Doula);
      const relationshipFromExtension = getExtensionValue(
        participant,
        Extensions.EncounterParticipantRelationshipToClient,
      );

      let relationshipToClient: string;
      if (typeof relationshipFromExtension === "string" && relationshipFromExtension.trim()) {
        relationshipToClient = relationshipFromExtension.trim();
      } else if (isDoula) {
        relationshipToClient = "Doula";
      } else {
        // Legacy others-present rows without the extension still need a valid form value.
        relationshipToClient = OTHERS_PRESENT_UNKNOWN_RELATIONSHIP;
      }

      const reference = participant.individual?.reference;
      return {
        name,
        relationshipToClient,
        ...(reference ? { reference } : {}),
      };
    }),
  );

  const resolvedSelections = selections.reduce<
    { name: string; relationshipToClient: string; reference?: string }[]
  >((others, selection) => {
    if (selection) others.push(selection);
    return others;
  }, []);

  if (resolvedSelections.length !== othersParticipants.length) {
    throw new Error(
      "Could not resolve all Others Present participants; refusing to preload a partial list",
    );
  }

  return resolvedSelections;
}

/**
 * Builds Others Present form rows from MultiSelect names, preserving prior
 * relationship selections when a name remains selected (case-insensitive).
 */
export function buildOthersPresentSelectionsFromNames(
  names: string[],
  previousSelections: {
    name: string;
    relationshipToClient?: string;
    reference?: string;
  }[] = [],
): { name: string; relationshipToClient?: string; reference?: string }[] {
  const uniqueNames = names.reduce<string[]>((unique, name) => {
    const normalizedName = name.trim().toLowerCase();
    if (!unique.some((existingName) => existingName.trim().toLowerCase() === normalizedName)) {
      unique.push(name);
    }
    return unique;
  }, []);

  const previousSelectionsByName = previousSelections.reduce<
    Map<string, { name: string; relationshipToClient?: string; reference?: string }>
  >((selectionsByName, person) => {
    const key = person.name.trim().toLowerCase();
    if (!selectionsByName.has(key)) selectionsByName.set(key, person);
    return selectionsByName;
  }, new Map());

  return uniqueNames.map((name) => {
    const previous = previousSelectionsByName.get(name.trim().toLowerCase());
    return previous ?? { name, relationshipToClient: undefined };
  });
}
